"use client";

import * as React from "react";
import * as TooltipPrimitive from "@radix-ui/react-tooltip";

import { cn } from "@/lib/utils.js";

const TooltipProvider = TooltipPrimitive.Provider;

// Self-wrap each Tooltip in its OWN Provider so a bare <Tooltip> never depends
// on an ancestor <TooltipProvider> being present in the same Radix module
// instance. This removes the "`Tooltip` must be used within `TooltipProvider`"
// crash that surfaced when a consumer (e.g. a Hub client-portal nav component)
// rendered a Tooltip whose provider context was missing/duplicated in the
// production chunk graph, or rendered outside the app's provider tree (error
// boundaries, portals). Matches the shadcn default + apps/hub local tooltip.
// Radix allows nested Providers, so an outer <TooltipProvider> stays harmless.
const Tooltip = ({
  ...props
}: React.ComponentProps<typeof TooltipPrimitive.Root>) => (
  <TooltipPrimitive.Provider>
    <TooltipPrimitive.Root {...props} />
  </TooltipPrimitive.Provider>
);
Tooltip.displayName = "Tooltip";

const TooltipTrigger = TooltipPrimitive.Trigger;

const TooltipContent = React.forwardRef<
  React.ElementRef<typeof TooltipPrimitive.Content>,
  React.ComponentPropsWithoutRef<typeof TooltipPrimitive.Content>
>(({ className, sideOffset = 4, ...props }, ref) => (
  <TooltipPrimitive.Content
    ref={ref}
    sideOffset={sideOffset}
    className={cn(
      "z-[1001] overflow-hidden rounded-md border-2 border-gray-200 dark:border-gray-700 bg-card dark:bg-card-900 px-3 py-1.5 text-sm text-card-foreground dark:text-gray-100 shadow-xl backdrop-blur-sm animate-in fade-in-0 zoom-in-95 data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=closed]:zoom-out-95 data-[side=bottom]:slide-in-from-top-2 data-[side=left]:slide-in-from-right-2 data-[side=right]:slide-in-from-left-2 data-[side=top]:slide-in-from-bottom-2",
      className,
    )}
    {...props}
  />
));
TooltipContent.displayName = TooltipPrimitive.Content?.displayName || 'TooltipContent';

export { Tooltip, TooltipTrigger, TooltipContent, TooltipProvider };
